import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Type definitions for validation
export interface ValidationProduct {
  id: string;
  name: string;
  category: string;
  imageUrl: string;
  price: number;
  sku?: string;
  brand?: string;
}

export interface DuplicateGroup {
  categoryName: string;
  categoryGroup: string;
  products: ValidationProduct[];
}

export interface ValidationResult {
  validProducts: ValidationProduct[];
  nonFashionProducts: Array<{ id: string; name: string; category: string }>;
  duplicateGroups?: DuplicateGroup[];
  requiresUserSelection: boolean;
  stats: {
    total: number;
    fashion: number;
    nonFashion: number;
    autoSelected: number;
    duplicates: number;
  };
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: ${fadeIn} 0.3s ease-out;
  padding: 20px;
`;

const Modal = styled.div`
  border-radius: 24px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  animation: ${slideUp} 0.4s ease-out;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  padding: 24px;
  border-bottom: 2px solid #f0f0f0;
  border-radius: 24px 24px 0 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: white;
  display: flex;
  align-items: center;
  gap: 12px;

  &::before {
    content: '⚠️';
    font-size: 14px;
  }
`;

const Subtitle = styled.p`
  margin: 8px 0 0 0;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
`;

const Content = styled.div`
  padding: 24px;
`;

const ConflictSection = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ConflictTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '📦';
    font-size: 20px;
  }
`;

const ConflictMessage = styled.p`
  font-size: 14px;
  color: #718096;
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
`;

const ProductCard = styled.div<{ selected: boolean }>`
  border: 3px solid ${props => props.selected ? '#667eea' : '#e2e8f0'};
  border-radius: 16px;
  padding: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.selected ? 'rgba(102, 126, 234, 0.05)' : 'white'};
  position: relative;

  &:hover {
    border-color: #667eea;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
  }

  ${props => props.selected && `
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  `}
`;

const SelectionBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: #667eea;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 8px;
`;

const ProductName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.3;
  min-height: 34px;
`;

const Footer = styled.div`
  padding: 20px 24px;
  border-top: 2px solid #f0f0f0;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  ` : `
    background: #e2e8f0;
    color: #2d3748;

    &:hover {
      background: #cbd5e0;
    }
  `}
`;

interface Props {
  validationResult: ValidationResult;
  onConfirm: (selectedProducts: ValidationProduct[]) => void;
  onCancel: () => void;
}

const ConflictResolutionModal: React.FC<Props> = ({ validationResult, onConfirm, onCancel }) => {
  const [selectedProducts, setSelectedProducts] = useState<Map<string, ValidationProduct>>(new Map());

  const handleProductSelect = (categoryGroup: string, product: ValidationProduct) => {
    const newSelected = new Map(selectedProducts);
    newSelected.set(categoryGroup, product);
    setSelectedProducts(newSelected);
  };

  const handleConfirm = () => {
    const selected = Array.from(selectedProducts.values());
    
    // Validation geçen ürünleri ekle
    const validNonDuplicates = validationResult.validProducts.filter(p => 
      !validationResult.duplicateGroups?.some(group => 
        group.products.some(gp => gp.id === p.id)
      )
    );

    const finalProducts = [...validNonDuplicates, ...selected];
    onConfirm(finalProducts);
  };

  const requiredSelections = validationResult.duplicateGroups?.length || 0;
  const madeSelections = selectedProducts.size;
  const canConfirm = (requiredSelections === 0 || madeSelections === requiredSelections);

  return (
    <Overlay onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <Modal>
        <Header>
          <Title>Ürün Seçimi Gerekli</Title>
          <Subtitle>
            Kombin oluşturabilmek için lütfen aşağıdaki ürünler arasından seçim yapın.
          </Subtitle>
        </Header>

        <Content>
          {/* Seçim grupları */}
          {validationResult.duplicateGroups?.map((group, groupIndex) => (
            <ConflictSection key={groupIndex}>
              <ConflictTitle>{group.categoryName}</ConflictTitle>
              
              <ProductGrid>
                {group.products.map(product => {
                  const isSelected = selectedProducts.get(group.categoryGroup)?.id === product.id;
                  
                  return (
                    <ProductCard
                      key={product.id}
                      selected={isSelected}
                      onClick={() => handleProductSelect(group.categoryGroup, product)}
                    >
                      {isSelected && <SelectionBadge>✓</SelectionBadge>}
                      <ProductImage 
                        src={product.imageUrl} 
                        alt={product.name}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder.jpg';
                        }}
                      />
                      <ProductName>{product.name}</ProductName>
                    </ProductCard>
                  );
                })}
              </ProductGrid>
            </ConflictSection>
          ))}

          {/* Seçim bilgisi */}
          {requiredSelections > 0 && (
            <ConflictMessage style={{ marginTop: '24px', textAlign: 'center', fontSize: '13px' }}>
              {madeSelections} / {requiredSelections} kategori seçildi
            </ConflictMessage>
          )}
        </Content>

        <Footer>
          <Button variant="secondary" onClick={onCancel}>
            İptal
          </Button>
          {(
            <Button 
              variant="primary" 
              onClick={handleConfirm}
              disabled={!canConfirm}
            >
              Kombin Oluştur
            </Button>
          )}
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default ConflictResolutionModal;

